import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * Width ladder for Frontify (media.ffycdn.net) gallery images — MUST match the
 * srcset ladder in snippets/ovi-img-src.liquid so warmed URLs are the same
 * cache keys the gallery's srcset requests at variant-switch time.
 */
const FRONTIFY_WARM_WIDTHS = [400, 800, 1200, 1600, 2000];

/**
 * Longest a variant switch keeps the previous gallery on screen while the new
 * one's images load. Warmed images decode in a few ms; on a slow line the swap
 * goes ahead after this and the slides fill in as they arrive.
 */
const IMAGE_READY_TIMEOUT_MS = 1200;

/** Marks a gallery swapped in by a variant change, so entry animations can skip it. */
const VARIANT_SWAP_ATTRIBUTE = 'data-variant-swap';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#scheduleVariantMediaPreload();
  }

  /** @type {Set<string>} URLs already queued so we don't re-preload across variant swaps. */
  static #preloadedUrls = new Set();

  /**
   * Warms the HTTP cache with the other variants' images so switching variants
   * feels instant. Deferred until after `window.load` and then again behind
   * `requestIdleCallback` with `fetchPriority: 'low'` so it never competes
   * with LCP / above-the-fold rendering — Lighthouse scores are unaffected
   * because all work happens after the load event.
   */
  #scheduleVariantMediaPreload() {
    // Same connection gate as variant-picker.js #scheduleVariantPrefetch —
    // warming dozens of images against Save-Data / 2G is hostile.
    const conn = /** @type {any} */ (navigator).connection;
    if (conn?.saveData) return;
    // '3g' included since 2026-08-27 (matches variant-picker.js): warming the
    // full variant image set (81 URLs ≈ 1.5MB sized on CS 12460) on a 3G-class
    // line starves the image the user actually clicked; each sized image is
    // ~23KB, so fetch-on-demand costs ~1-2s there instead.
    if (conn && ['slow-2g', '2g', '3g'].includes(conn.effectiveType)) return;

    const script = this.querySelector('script[type="application/json"][data-variant-media-preload]');
    if (!script || !script.textContent) return;

    /** @type {string[]} */
    let urls;
    try {
      urls = JSON.parse(script.textContent);
    } catch {
      return;
    }
    if (!Array.isArray(urls) || urls.length === 0) return;

    const fresh = urls.filter((url) => typeof url === 'string' && url && !MediaGallery.#preloadedUrls.has(url));
    if (fresh.length === 0) return;
    for (const url of fresh) MediaGallery.#preloadedUrls.add(url);

    const { signal } = this.#controller;
    const run = () => {
      // Resolved lazily (post-load) so the stage has its final layout size.
      const warmWidth = this.#frontifyWarmWidth();
      // Trickle two at a time instead of firing all (~81 on CS 12460, ~1.5MB
      // sized) at once — a parallel blast shares the pipe with the image of a
      // variant the user just clicked (measured 8s for a 21KB image on a
      // Fast-3G line while the warm ran). Mirrors variant-picker.js
      // #prefetchAlternates. fetchPriority stays low; the chain stops at the
      // gallery's abort signal like the loop did.
      const queue = fresh.slice();
      const startNext = () => {
        if (signal.aborted) return;
        const url = queue.shift();
        if (!url) return;
        const img = new Image();
        img.decoding = 'async';
        // fetchPriority is read-only on some older builds; assign defensively.
        try {
          img.fetchPriority = 'low';
        } catch {
          // no-op
        }
        img.onload = startNext;
        img.onerror = startNext;
        if (url.includes('media.ffycdn.net')) {
          img.src = `${url}${url.includes('?') ? '&' : '?'}width=${warmWidth}&format=webp`;
        } else {
          img.src = url;
        }
      };
      startNext();
      startNext();
    };

    const schedule = () => {
      if (signal.aborted) return;
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(run, { timeout: 3000 });
      } else {
        setTimeout(run, 1500);
      }
    };

    if (document.readyState === 'complete') {
      schedule();
    } else {
      window.addEventListener('load', schedule, { once: true, signal });
    }
  }

  /**
   * The Frontify width the gallery's srcset will pick for the stage after a
   * variant switch: rendered stage width × DPR, snapped UP the shared ladder
   * (browsers choose the smallest candidate ≥ the needed width). Warming full
   * originals instead used to pull ~13MB per PDP (audit 2026-08-25).
   */
  #frontifyWarmWidth() {
    const stage = this.querySelector('slideshow-container .product-media__image');
    const cssWidth = stage instanceof HTMLElement && stage.clientWidth > 0 ? stage.clientWidth : 800;
    const target = cssWidth * (window.devicePixelRatio || 1);
    return FRONTIFY_WARM_WIDTHS.find((w) => w >= target) ?? FRONTIFY_WARM_WIDTHS[FRONTIFY_WARM_WIDTHS.length - 1];
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /** Bumped per variant update so a slow earlier swap never lands after a newer one. */
  #updateToken = 0;

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * The swap waits until the new gallery's stage images are decoded (capped by
   * IMAGE_READY_TIMEOUT_MS). Frontify (`ovi-`) images carry no width/height and
   * the gallery uses `aspect_ratio: adapt`, so a freshly inserted slide is 0px
   * tall until its image arrives: the quick-add column collapsed from 765px to
   * 394px and grew back to 1019px on every colour click (SPI-37, measured
   * 2026-11-02). With the images already decoded, the new slides lay out at
   * their final height in the same frame as the swap.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = async (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    const token = ++this.#updateToken;
    await MediaGallery.#imagesReady(newMediaGallery);
    if (token !== this.#updateToken || !this.isConnected) return;

    newMediaGallery.setAttribute(VARIANT_SWAP_ATTRIBUTE, '');
    this.replaceWith(newMediaGallery);
  };

  /**
   * Fetches and decodes the stage images of a not-yet-inserted gallery, then
   * marks them eager: a lazy image waits for an intersection check even when
   * cached, which would show one collapsed frame after the swap. Images with
   * width/height (Shopify media) already reserve their box, so they are left alone.
   *
   * @param {Element} gallery - The incoming media gallery (from the parsed section HTML).
   * @returns {Promise<void>}
   */
  static async #imagesReady(gallery) {
    const images = [...gallery.querySelectorAll('img.product-media__image')].filter(
      (img) => !img.closest('zoom-dialog') && !(img.hasAttribute('width') && img.hasAttribute('height'))
    );
    if (images.length === 0) return;

    const decoded = images.map((img) => {
      const probe = new Image();
      probe.decoding = 'async';
      // sizes before srcset so the probe picks the same candidate (and cache key) as the slide.
      const sizes = img.getAttribute('sizes');
      const srcset = img.getAttribute('srcset');
      const src = img.getAttribute('src');
      if (sizes) probe.sizes = sizes;
      if (srcset) probe.srcset = srcset;
      if (src) probe.src = src;
      img.setAttribute('loading', 'eager');
      // A broken image must not hold the swap back; it renders as it would have.
      return probe.decode().catch(() => {});
    });

    await Promise.race([
      Promise.all(decoded),
      new Promise((resolve) => setTimeout(resolve, IMAGE_READY_TIMEOUT_MS)),
    ]);
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
